import { useEffect, useRef, useState } from 'react';
import { theme } from '../theme.js';
import { Icon } from './Icon.js';

type Tool = 'pen' | 'arrow' | 'circle' | 'text';
type Pt = { x: number; y: number };
type Shape =
  | { kind: 'pen'; color: string; pts: Pt[] }
  | { kind: 'arrow' | 'circle'; color: string; a: Pt; b: Pt }
  | { kind: 'text'; color: string; at: Pt; text: string };

const COLORS = ['#e5352f', '#ffd400', '#ffffff'];
/** Long side of the saved photo: plenty for evidence, quick to upload. */
const MAX_SIDE = 1600;

/**
 * Draw on a photo before it's saved: circle or arrow the problem, scribble,
 * or add a short label. Done saves the marked-up copy (the photo as taken
 * is kept too); "Use as is" skips it. If the phone can't open the photo
 * here (some HEIC files), it's used as is straight away.
 */
export function PhotoMarkup({ file, onDone, onSkip, onCancel }: {
  file: File;
  onDone: (marked: Blob) => void;
  onSkip: () => void;
  onCancel: () => void;
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [base, setBase] = useState<ImageBitmap | HTMLImageElement | null>(null);
  const [size, setSize] = useState<{ w: number; h: number } | null>(null);
  const [tool, setTool] = useState<Tool>('circle');
  const [color, setColor] = useState(COLORS[0]!);
  const [shapes, setShapes] = useState<Shape[]>([]);
  // The stroke being drawn. Pointer events can arrive faster than React
  // re-renders, so handlers read and write the ref; state just redraws.
  const [draft, setDraftState] = useState<Shape | null>(null);
  const draftRef = useRef<Shape | null>(null);
  const setDraft = (d: Shape | null) => { draftRef.current = d; setDraftState(d); };
  const [textAt, setTextAt] = useState<Pt | null>(null);
  const [text, setText] = useState('');
  const [saving, setSaving] = useState(false);

  // Open the photo the right way up, scaled down.
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const img = await loadImage(file);
        if (!alive) return;
        const w0 = 'naturalWidth' in img ? img.naturalWidth : img.width;
        const h0 = 'naturalHeight' in img ? img.naturalHeight : img.height;
        const k = Math.min(1, MAX_SIDE / Math.max(w0, h0));
        setBase(img);
        setSize({ w: Math.round(w0 * k), h: Math.round(h0 * k) });
      } catch {
        if (alive) onSkip(); // can't open it here: use it as is
      }
    })();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [file]);

  const line = size ? Math.max(4, Math.round(Math.max(size.w, size.h) / 170)) : 6;

  useEffect(() => {
    const cv = canvasRef.current;
    if (!cv || !base || !size) return;
    const ctx = cv.getContext('2d')!;
    ctx.drawImage(base, 0, 0, size.w, size.h);
    for (const s of draft ? [...shapes, draft] : shapes) draw(ctx, s, line);
  }, [base, size, shapes, draft, line]);

  function point(e: React.PointerEvent): Pt {
    const r = canvasRef.current!.getBoundingClientRect();
    return { x: ((e.clientX - r.left) / r.width) * size!.w, y: ((e.clientY - r.top) / r.height) * size!.h };
  }

  function down(e: React.PointerEvent) {
    if (!size || textAt) return;
    const p = point(e);
    if (tool === 'text') { setTextAt(p); setText(''); return; }
    try { (e.target as Element).setPointerCapture(e.pointerId); } catch { /* keeps drawing without it */ }
    setDraft(tool === 'pen' ? { kind: 'pen', color, pts: [p] } : { kind: tool, color, a: p, b: p });
  }
  function move(e: React.PointerEvent) {
    const d = draftRef.current;
    if (!d) return;
    const p = point(e);
    setDraft(d.kind === 'pen' ? { ...d, pts: [...d.pts, p] } : d.kind === 'text' ? d : { ...d, b: p });
  }
  function up(e: React.PointerEvent) {
    const d0 = draftRef.current;
    if (!d0) return;
    const p = point(e);
    const d: Shape = d0.kind === 'pen' ? { ...d0, pts: [...d0.pts, p] } : d0.kind === 'text' ? d0 : { ...d0, b: p };
    const tiny = d.kind === 'pen' ? d.pts.length < 2 : d.kind !== 'text' && Math.hypot(d.b.x - d.a.x, d.b.y - d.a.y) < line * 2;
    if (!tiny) setShapes((list) => [...list, d]);
    setDraft(null);
  }

  function addText() {
    const at = textAt, label = text.trim().slice(0, 60);
    if (at && label) setShapes((list) => [...list, { kind: 'text', color, at, text: label }]);
    setTextAt(null);
    setText('');
  }

  function done() {
    if (!shapes.length) { onSkip(); return; }
    setSaving(true);
    canvasRef.current!.toBlob((b) => { if (b) onDone(b); else onSkip(); }, 'image/jpeg', 0.85);
  }

  return (
    <div role="dialog" aria-label="Mark up photo" style={{
      position: 'fixed', inset: 0, zIndex: 50, background: '#111', display: 'flex', flexDirection: 'column', fontFamily: theme.font,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 12px', color: '#fff' }}>
        <button onClick={onCancel} aria-label="Cancel" style={darkBtn}><Icon name="x" size={18} /></button>
        <div style={{ flex: 1, fontSize: 15, fontWeight: 700 }}>Mark the problem</div>
        <button onClick={onSkip} disabled={saving} style={{ ...darkBtn, width: 'auto', padding: '0 12px', fontSize: 13, fontWeight: 600 }}>Use as is</button>
        <button onClick={done} disabled={saving || !size} style={{ ...darkBtn, width: 'auto', padding: '0 14px', background: theme.status.ok.dot, fontSize: 14, fontWeight: 700 }}>
          {saving ? 'Saving…' : 'Done'}
        </button>
      </div>

      <div style={{ flex: 1, minHeight: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 8, position: 'relative' }}>
        {!size && <div style={{ color: '#aaa', fontSize: 13 }}>Opening photo…</div>}
        {size && (
          <canvas ref={canvasRef} width={size.w} height={size.h}
            onPointerDown={down} onPointerMove={move} onPointerUp={up} onPointerCancel={up}
            style={{ maxWidth: '100%', maxHeight: '100%', touchAction: 'none', borderRadius: 6, cursor: 'crosshair' }} />
        )}
        {textAt && (
          <form onSubmit={(e) => { e.preventDefault(); addText(); }} style={{
            position: 'absolute', left: 12, right: 12, bottom: 12, display: 'flex', gap: 8,
            background: '#222', padding: 8, borderRadius: 10,
          }}>
            <input autoFocus value={text} onChange={(e) => setText(e.target.value)} maxLength={60} placeholder="Label, e.g. leak here"
              style={{ flex: 1, minWidth: 0, padding: '9px 10px', borderRadius: 8, border: 'none', fontSize: 15, fontFamily: theme.font }} />
            <button type="button" onClick={() => setTextAt(null)} style={{ ...darkBtn, width: 'auto', padding: '0 10px', fontSize: 13 }}>Cancel</button>
            <button type="submit" style={{ ...darkBtn, width: 'auto', padding: '0 12px', background: theme.accent, fontSize: 13, fontWeight: 700 }}>Add</button>
          </form>
        )}
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: '10px 12px calc(10px + env(safe-area-inset-bottom))' }}>
        <div style={{ display: 'flex', gap: 6 }}>
          {([['circle', 'Circle'], ['arrow', 'Arrow'], ['pen', 'Draw'], ['text', 'Text']] as [Tool, string][]).map(([t, label]) => (
            <button key={t} onClick={() => setTool(t)} aria-pressed={tool === t} style={{
              ...darkBtn, flex: 1, width: 'auto', fontSize: 13, fontWeight: 700,
              background: tool === t ? '#fff' : '#2a2a2a', color: tool === t ? '#111' : '#fff',
            }}>{label}</button>
          ))}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {COLORS.map((c) => (
            <button key={c} onClick={() => setColor(c)} aria-label={`Colour ${c}`} aria-pressed={color === c} style={{
              width: 30, height: 30, borderRadius: 15, background: c, cursor: 'pointer', flexShrink: 0,
              border: color === c ? '3px solid #fff' : '2px solid #555', boxShadow: color === c ? '0 0 0 2px #111 inset' : 'none',
            }} />
          ))}
          <div style={{ flex: 1 }} />
          <button onClick={() => setShapes(shapes.slice(0, -1))} disabled={!shapes.length} style={{
            ...darkBtn, width: 'auto', padding: '0 16px', fontSize: 13, fontWeight: 700, opacity: shapes.length ? 1 : 0.4,
          }}>Undo</button>
        </div>
      </div>
    </div>
  );
}

function draw(ctx: CanvasRenderingContext2D, s: Shape, line: number) {
  ctx.save();
  ctx.strokeStyle = s.color;
  ctx.fillStyle = s.color;
  ctx.lineWidth = line;
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  // A dark edge keeps marks readable on light and dark backgrounds.
  ctx.shadowColor = 'rgba(0,0,0,0.55)';
  ctx.shadowBlur = line * 0.8;
  if (s.kind === 'pen') {
    ctx.beginPath();
    s.pts.forEach((p, i) => (i ? ctx.lineTo(p.x, p.y) : ctx.moveTo(p.x, p.y)));
    ctx.stroke();
  } else if (s.kind === 'circle') {
    ctx.beginPath();
    ctx.ellipse((s.a.x + s.b.x) / 2, (s.a.y + s.b.y) / 2, Math.abs(s.b.x - s.a.x) / 2, Math.abs(s.b.y - s.a.y) / 2, 0, 0, Math.PI * 2);
    ctx.stroke();
  } else if (s.kind === 'arrow') {
    const ang = Math.atan2(s.b.y - s.a.y, s.b.x - s.a.x);
    const head = line * 4;
    ctx.beginPath();
    ctx.moveTo(s.a.x, s.a.y);
    ctx.lineTo(s.b.x, s.b.y);
    ctx.moveTo(s.b.x, s.b.y);
    ctx.lineTo(s.b.x - head * Math.cos(ang - 0.5), s.b.y - head * Math.sin(ang - 0.5));
    ctx.moveTo(s.b.x, s.b.y);
    ctx.lineTo(s.b.x - head * Math.cos(ang + 0.5), s.b.y - head * Math.sin(ang + 0.5));
    ctx.stroke();
  } else if (s.kind === 'text') {
    const size = line * 5;
    ctx.font = `700 ${size}px Inter, system-ui, sans-serif`;
    const w = ctx.measureText(s.text).width;
    ctx.shadowBlur = 0;
    ctx.fillStyle = 'rgba(0,0,0,0.6)';
    ctx.fillRect(s.at.x - size * 0.3, s.at.y - size * 1.05, w + size * 0.6, size * 1.4);
    ctx.fillStyle = s.color;
    ctx.fillText(s.text, s.at.x, s.at.y);
  }
  ctx.restore();
}

/** The photo, rotated as the camera meant it. */
async function loadImage(file: File): Promise<ImageBitmap | HTMLImageElement> {
  if ('createImageBitmap' in window) {
    try { return await createImageBitmap(file, { imageOrientation: 'from-image' }); } catch { /* try an <img> */ }
  }
  const url = URL.createObjectURL(file);
  try {
    const img = new Image();
    img.src = url;
    await img.decode();
    return img;
  } finally {
    setTimeout(() => URL.revokeObjectURL(url), 0);
  }
}

const darkBtn: React.CSSProperties = {
  height: 36, width: 36, borderRadius: 10, border: 'none', background: '#2a2a2a', color: '#fff',
  display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', fontFamily: theme.font, flexShrink: 0,
};
